import {
  AdminSpinner,
  Button,
  Callout,
  Disclaimer,
  Div,
  H5,
  Icon,
  Modal,
  P,
  PlusIcon,
  PropsModal,
  Radio,
  SuccessToast,
  TaxRateFormModal,
  TaxRateRow,
  lightTheme,
  reportError,
  updateMenuItemTaxRates,
  updateMenuModifierTaxRates,
  useLocationTaxRates,
} from '@vgrubs/components';
import { MenuV2Response } from 'components/src/types/menu.types';
import {
  LocationTaxRateEntity,
  TaxRateEntity,
} from 'components/src/types/taxRate.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router';

/** One name for one action: the button and the empty-selection save share it. */
const REMOVE_LABEL = 'Remove Custom Taxes';

/** What the picker is about to write taxes to. */
export type MenuTaxRatesTarget = {
  kind: 'item' | 'option';
  id: number | string;
  name: string;
  /** The effective list, as the menu returned it. */
  taxRates: TaxRateEntity[];
  /** True when the list above is already this item's own choice. */
  taxRatesOverridden: boolean;
};

type Props = PropsModal & {
  /** Null keeps the modal closed. */
  target: MenuTaxRatesTarget | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
};

/**
 * Picks which of the location's taxes an item or option is published with.
 *
 * Never opened on a POS location: taxes are synced from the register there and
 * every write answers 400, so those screens show the list without a way in.
 *
 * Two rules the copy here has to carry, because neither is guessable from the
 * ticks:
 *
 * - A selection REPLACES the location's rates for this item, it does not add
 *   to them. That is why the effective list is ticked even when nothing has
 *   been overridden yet - unticking one is how you drop it.
 * - An empty selection means "inherit", not "no tax". A tax free item is a 0%
 *   rate on the location, ticked here. So does ticking exactly the location's
 *   applies-to-all rates, which is written as empty rather than as a copy.
 * - A rate can be deleted from the location and still be set on the item. Those
 *   get their own section, so what is ticked is always what the item charges.
 */
export const MenuTaxRatesModal = ({
  target,
  closeModal,
  setMenu,
  setLoading,
  handleChangeShouldPublish,
}: Props) => {
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  const { taxRates, isLoading, invalidate } = useLocationTaxRates(locationId, {
    enabled: !!target,
  });

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [addModal, setAddModal] = useState(false);

  // Seeded from the effective list, whether it is inherited or the item's own:
  // what is ticked on open is always what the item is published with today.
  useEffect(() => {
    setSelectedIds(target ? target.taxRates.map((taxRate) => taxRate.id) : []);
  }, [target]);

  const toggle = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  /**
   * A rate added from here is ticked straight away - that is why it was added.
   * It still takes a Confirm to reach the item, like any other change.
   */
  const handleTaxRateCreated = (taxRate: LocationTaxRateEntity) => {
    invalidate();
    setSelectedIds((prev) =>
      prev.includes(taxRate.id) ? prev : [...prev, taxRate.id]
    );
  };

  async function handleSubmit(taxRateIds: number[]) {
    if (!target) return;

    const update =
      target.kind === 'item'
        ? updateMenuItemTaxRates
        : updateMenuModifierTaxRates;

    try {
      setLoading(true);
      const { data } = await update(locationId, brandId, target.id, {
        taxRateIds,
      });

      setMenu(data.data);
      handleChangeShouldPublish(true);
      SuccessToast(data.message);
      closeModal?.();
    } catch (error) {
      reportError(error, { context: 'updateMenuTaxRates' });
    } finally {
      setLoading(false);
    }
  }

  const noun = target?.kind === 'option' ? 'option' : 'item';

  /** What inheriting gives the item: every rate the location applies to all. */
  const inheritedIds = taxRates
    .filter((taxRate) => taxRate.applyToAllItems)
    .map((taxRate) => taxRate.id);

  /**
   * Rates this item carries that the location no longer lists. Deleting a rate
   * is a soft delete - it leaves the location's list, the assignments that used
   * it stay, and the item goes on being published with it. They get their own
   * rows below, because otherwise the picker shows a selection the item does
   * not have: two items can look identical here while only one is flagged.
   */
  const removedTaxRates = target
    ? target.taxRates.filter(
        (taxRate) => !taxRates.some((rate) => rate.id === taxRate.id)
      )
    : [];

  /**
   * An empty selection means "inherit". A selection that happens to match what
   * the item would inherit means the same thing spelled out, so it is written
   * as empty too - otherwise the item freezes today's rates behind a "Custom
   * taxes" flag that shows no difference anywhere, and stops following the
   * location the next time its rates change.
   */
  const inherits =
    selectedIds.length === 0 ||
    (selectedIds.length === inheritedIds.length &&
      inheritedIds.every((id) => selectedIds.includes(id)));
  /** What the button writes, which is not always what is ticked. */
  const taxRateIds = inherits ? [] : selectedIds;

  /** Saving a selection that inherits is the same write as the remove button. */
  const removesOverride = !!target?.taxRatesOverridden && inherits;
  const showRemoveButton = !!target?.taxRatesOverridden && !inherits;

  return (
    <>
      <Modal
        isOpen={!!target}
        closeModal={closeModal}
        header="Taxes"
        subtitle={target?.name}
        logo={false}
        zIndex={110}
        footer={
          <>
            <Button
              $selected
              $w="100%"
              $mb={showRemoveButton ? '16' : '0'}
              onClick={() => handleSubmit(taxRateIds)}
            >
              {/* Saving a selection that leaves the item inheriting IS
              removing the override, so it says so rather than "Confirm" - the
              same action never goes by two names in this modal. */}
              {removesOverride ? REMOVE_LABEL : 'Confirm'}
            </Button>
            {showRemoveButton && (
              <Button
                $selected
                $gray
                $w="100%"
                onClick={() => handleSubmit([])}
              >
                {REMOVE_LABEL}
              </Button>
            )}
          </>
        }
      >
        {/* Nothing is rendered until the location's list is in: every row
        here is matched against it, and a half-loaded list would file rates
        under "no longer on the location". */}
        {isLoading ? (
          <AdminSpinner />
        ) : (
          <Div $flex $column $gap="16px">
            {taxRates.length > 0 &&
              (target?.taxRatesOverridden ? (
                <Callout tone="info">
                  This {noun} has taxes of its own, so the location&apos;s do
                  not apply to it. Removing them puts it back on whatever the
                  location charges, today and whenever that changes.
                </Callout>
              ) : (
                <Callout tone="info">
                  These are the location&apos;s taxes. Changing the selection
                  gives this {noun} its own, and the location&apos;s stop
                  applying to it.
                </Callout>
              ))}

            <div>
              <Div $flex $alignCenter $between $mb="8">
                <H5 $m="0" $left>
                  Location taxes
                </H5>
                <Icon $pointer onClick={() => setAddModal(true)}>
                  <PlusIcon />
                </Icon>
              </Div>

              {taxRates.length === 0 ? (
                <P $m="24px 0" $center>
                  This location has no taxes yet.
                </P>
              ) : (
                taxRates.map((taxRate) => (
                  <TaxRateRow
                    key={taxRate.id}
                    taxRate={taxRate}
                    onClick={() => toggle(taxRate.id)}
                    lead={<Radio checked={selectedIds.includes(taxRate.id)} />}
                  />
                ))
              )}
            </div>

            {removedTaxRates.length > 0 && (
              <div>
                <H5 $m="0 0 8px" $left>
                  No longer on the location
                </H5>

                {removedTaxRates.map((taxRate) => (
                  <TaxRateRow
                    key={taxRate.id}
                    taxRate={taxRate}
                    onClick={() => toggle(taxRate.id)}
                    lead={<Radio checked={selectedIds.includes(taxRate.id)} />}
                  />
                ))}

                <Disclaimer $m="8px 0 0" $left $color={lightTheme.darkGray}>
                  Deleted from the location while this {noun} was still set on
                  them, so it is published with them until they are unticked.
                </Disclaimer>
              </div>
            )}

            {taxRates.length > 0 && (
              <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
                Nothing ticked means this {noun} falls back to the
                location&apos;s taxes. To publish it tax free, add a 0% tax with
                the plus above and tick that instead.
              </Disclaimer>
            )}
          </Div>
        )}
      </Modal>

      {/* A sibling, not a child: the modal box is a transformed motion element,
      and a fixed-position modal nested inside it would position against that
      instead of the viewport. `zIndex` clears the picker's own 110. */}
      <TaxRateFormModal
        formModal={addModal}
        closeModal={() => setAddModal(false)}
        locationId={locationId}
        setLoading={setLoading}
        onSaved={handleTaxRateCreated}
        zIndex={120}
      />
    </>
  );
};
